import { Component, OnInit, ViewChild } from '@angular/core';
import { formatUtcDate } from '../../shared/date-utils';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UsersService } from '../../services/users.service';
import { UserDto } from '../../interfaces/common/userDto';

import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { LeftMenuComponent } from '../../menu/left-menu/left-menu.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { UsersListSkeletonComponent } from './users-list-skeleton/users-list-skeleton.component';

interface ColumnDef {
  key: string;
  label: string;
}

@Component({
  selector: 'modernization-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  imports: [FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, UsersListSkeletonComponent]
})
export class UsersListComponent implements OnInit {
  isLoading = false;
  error: string | null = null;

  displayedColumns: ColumnDef[] = [
    { key: 'id', label: 'ID' },
    { key: 'userPrincipalName', label: 'UPN' },
    { key: 'email', label: 'Email' },
    { key: 'name', label: 'Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'isActive', label: 'Active' },
    { key: 'lastRoleSyncAtUtc', label: 'Last Role Sync (UTC)' },
    { key: 'lastRoleSyncSource', label: 'Last Role Sync Source' },
    { key: 'lastRoleUpdateAtUtc', label: 'Last Role Update (UTC)' },
    { key: 'isAdmin', label: 'Admin' },
    { key: 'isOperationsManager', label: 'Operations Manager' },
    { key: 'isEnterpriseLead', label: 'Enterprise Lead' },
    { key: 'isProjectLead', label: 'Project Lead' },
    { key: 'isAmore', label: 'Amore Team' },
  ];

  displayedColumnsKeys = this.displayedColumns.map((col) => col.key);
  users = new MatTableDataSource<UserDto>([]);

  private paginator: MatPaginator | null = null;
  private sort: MatSort | null = null;

  @ViewChild(MatPaginator)
  set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    this.applyTableFeatures();
  }

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    this.sort = sort;
    this.applyTableFeatures();
  }

  constructor(private usersService: UsersService) {}

  ngOnInit(): void {
    this.users.sortingDataAccessor = (item: any, property: string) => {
      const raw = item?.[property];
      if (raw === null || raw === undefined) {
        return '';
      }
      if (this.isDateColumn(property)) {
        const d = new Date(raw);
        const t = d.getTime();
        return Number.isNaN(t) ? '' : t;
      }
      if (typeof raw === 'boolean') {
        return raw ? 1 : 0;
      }
      if (typeof raw === 'number') {
        return raw;
      }
      return String(raw).toLowerCase();
    };

    this.loadUsers();
  }

  private applyTableFeatures(): void {
    // Table is behind *ngIf, so paginator/sort can appear after initial lifecycle hooks.
    if (this.paginator) {
      this.users.paginator = this.paginator;
    }
    if (this.sort) {
      this.users.sort = this.sort;
    }
  }

  loadUsers(): void {
    this.isLoading = true;
    this.error = null;

    this.usersService.getAllUsers().subscribe({
      next: (users) => {
        this.users.data = users ?? [];
        this.isLoading = false;
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('Error loading users:', err);
        this.error = 'Failed to load users list';
        this.isLoading = false;
      },
    });
  }

  isBooleanColumn(key: string): boolean {
    return [
      'isActive',
      'isAdmin',
      'isOperationsManager',
      'isEnterpriseLead',
      'isProjectLead',
      'isAmore',
    ].includes(key);
  }

  isDateColumn(key: string): boolean {
    return ['lastRoleSyncAtUtc', 'lastRoleUpdateAtUtc'].includes(key);
  }

  formatUtcDate(value: any): string {
    if (!value) {
      return 'N/A';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return 'N/A';
    }
    return formatUtcDate(d, 'short');
  }
}
